import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, takeUntil } from "rxjs";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { parseTdgExcelImportResult } from "../../../core/utils/tdg-excel-import-result.util";
import {
  ImportResultConfig,
  ImportResultModalComponent,
} from "../import-result-modal/import-result-modal.component";

export interface ImportTestDataModalResult {
  testSuiteId: string;
  testSuiteName: string;
  testCases: any[];
  fileType: string;
  file: any;
  message?: string;
}

interface TestCaseOption {
  _id: string;
  testCaseId: string;
  testCase: string;
}

@Component({
  selector: "app-import-test-data-modal",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./import-test-data-modal.component.html",
  styleUrl: "./import-test-data-modal.component.scss",
})
export class ImportTestDataModalComponent implements OnInit, OnDestroy {
  @Input() projectId = "";

  readonly activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);
  private readonly toaster = inject(ToasterService);
  private readonly el = inject(ElementRef);
  private readonly modalService = inject(NgbModal);
  private readonly destroy$ = new Subject<void>();

  testSuites: any[] = [];
  selectedSuiteId = "";
  isLoadingSuites = false;
  suitesError = false;

  testCaseOptions: TestCaseOption[] = [];
  selectedTestCaseIds = new Set<string>();
  isLoadingTestCases = false;
  testCasesError = false;
  isTestCaseDropdownOpen = false;

  newFileType = "XLSX";
  newFile: File | null = null;
  isUploading = false;
  uploadError = "";
  isDragOver = false;

  isSubmitting = false;
  readonly maxVisibleChips = 3;

  private readonly uploadAcceptExtensions = [".xlsx", ".xls"];

  get selectedSuiteName(): string {
    const suite = this.testSuites.find(
      (s) => String(s._id) === this.selectedSuiteId
    );
    return suite?.versionName || suite?.name || "";
  }

  get isAllTestCasesSelected(): boolean {
    return (
      this.testCaseOptions.length > 0 &&
      this.selectedTestCaseIds.size === this.testCaseOptions.length
    );
  }

  get isSomeTestCasesSelected(): boolean {
    return this.selectedTestCaseIds.size > 0 && !this.isAllTestCasesSelected;
  }

  get selectedTestCasesList(): TestCaseOption[] {
    return this.testCaseOptions.filter((tc) =>
      this.selectedTestCaseIds.has(tc._id)
    );
  }

  get selectedTestCaseCount(): number {
    return this.selectedTestCaseIds.size;
  }

  get visibleSelectedTestCases(): TestCaseOption[] {
    return this.selectedTestCasesList.slice(0, this.maxVisibleChips);
  }

  get overflowTestCaseCount(): number {
    return Math.max(0, this.selectedTestCaseCount - this.maxVisibleChips);
  }

  get testCaseDropdownLabel(): string {
    if (!this.selectedSuiteId) {
      return "Select test suite first";
    }
    if (this.isLoadingTestCases) {
      return "Loading…";
    }
    if (!this.testCaseOptions.length) {
      return "No test cases available";
    }
    return "Select test case";
  }

  get newFileAccept(): string {
    return this.uploadAcceptExtensions.join(",");
  }

  get canAdd(): boolean {
    if (this.isLoadingSuites || this.isSubmitting || this.isUploading) {
      return false;
    }
    return !!this.selectedSuiteId && !!this.newFile;
  }

  ngOnInit(): void {
    this.loadTestSuites();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event): void {
    if (!this.isTestCaseDropdownOpen) {
      return;
    }
    const multiselect: HTMLElement | null =
      this.el.nativeElement.querySelector(".itd-multiselect");
    const target = event.target as Node | null;
    if (multiselect && target && !multiselect.contains(target)) {
      this.isTestCaseDropdownOpen = false;
    }
  }

  onSuiteChange(): void {
    this.selectedTestCaseIds = new Set();
    this.testCaseOptions = [];
    this.testCasesError = false;
    this.isTestCaseDropdownOpen = false;
    if (this.selectedSuiteId) {
      this.loadTestCases(this.selectedSuiteId);
    }
  }

  toggleTestCaseDropdown(event: Event): void {
    event.stopPropagation();
    if (!this.selectedSuiteId || this.isLoadingTestCases) {
      return;
    }
    this.isTestCaseDropdownOpen = !this.isTestCaseDropdownOpen;
  }

  isTestCaseSelected(id: string): boolean {
    return this.selectedTestCaseIds.has(id);
  }

  toggleAllTestCases(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isAllTestCasesSelected) {
      this.selectedTestCaseIds = new Set();
    } else {
      this.selectedTestCaseIds = new Set(
        this.testCaseOptions.map((tc) => tc._id)
      );
    }
  }

  removeTestCase(id: string, event?: Event): void {
    event?.stopPropagation();
    if (!this.selectedTestCaseIds.has(id)) {
      return;
    }
    const next = new Set(this.selectedTestCaseIds);
    next.delete(id);
    this.selectedTestCaseIds = next;
  }

  toggleTestCase(event: Event, id: string): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.selectedTestCaseIds.has(id)) {
      this.selectedTestCaseIds.delete(id);
    } else {
      this.selectedTestCaseIds.add(id);
    }
    this.selectedTestCaseIds = new Set(this.selectedTestCaseIds);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setNewFile(file);
    }
  }

  onNewFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.setNewFile(file);
    }
  }

  cancel(): void {
    this.activeModal.dismiss("cancel");
  }

  add(): void {
    if (!this.canAdd || !this.projectId || !this.newFile) {
      return;
    }

    this.isSubmitting = true;
    this.uploadError = "";
    const uploadedFile = this.newFile;
    const uploadedType = this.newFileType;
    const caseIds = this.selectedTestCasesList.map((tc) => tc.testCaseId);
    const caseIdsPayload = caseIds.length > 0 ? caseIds : undefined;

    this.restService
      .importExistingData(
        this.projectId,
        this.selectedSuiteId,
        uploadedFile,
        caseIdsPayload
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: unknown) => {
          this.isSubmitting = false;
          const config = parseTdgExcelImportResult(response);
          if (config.type === "success") {
            this.closeWithResult(
              { connectionName: uploadedFile.name, meta: { fileName: uploadedFile.name } },
              uploadedType,
              config.subTitle
            );
            return;
          }
          if (config.type === "partial") {
            this.closeWithResult(
              { connectionName: uploadedFile.name, meta: { fileName: uploadedFile.name } },
              uploadedType,
              config.subTitle
            );
            this.openResultModal(config);
            return;
          }
          this.uploadError = "Failed to import test data file.";
          this.openResultModal(config);
        },
        error: (err: unknown) => {
          this.isSubmitting = false;
          this.uploadError = "Failed to import test data file.";
          this.openResultModal(parseTdgExcelImportResult(err, { forceError: true }));
        },
      });
  }

  private openResultModal(config: ImportResultConfig): void {
    const ref = this.modalService.open(ImportResultModalComponent, {
      centered: true,
      backdrop: "static",
      keyboard: false,
      size: "m",
    });
    ref.componentInstance.config = config;
  }

  private closeWithResult(file: any, fileType: string, message?: string): void {
    const result: ImportTestDataModalResult = {
      testSuiteId: this.selectedSuiteId,
      testSuiteName: this.selectedSuiteName,
      testCases: this.selectedTestCasesList,
      fileType,
      file,
      message,
    };
    this.activeModal.close(result);
  }

  private setNewFile(file: File): void {
    this.uploadError = "";
    const name = file.name.toLowerCase();
    const valid = this.uploadAcceptExtensions.some((ext) => name.endsWith(ext));
    if (!valid) {
      this.uploadError = "Only .xlsx and .xls files are allowed.";
      this.newFile = null;
      return;
    }

    this.newFileType = "XLSX";
    this.newFile = file;
  }

  private loadTestSuites(): void {
    if (!this.projectId) {
      return;
    }
    this.isLoadingSuites = true;
    this.suitesError = false;
    this.restService
      .getTestCaseVersions(this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.testSuites = Array.isArray(response?.data) ? response.data : [];
          this.isLoadingSuites = false;
        },
        error: () => {
          this.isLoadingSuites = false;
          this.suitesError = true;
          this.testSuites = [];
        },
      });
  }

  private loadTestCases(versionId: string): void {
    if (!this.projectId || !versionId) {
      return;
    }
    this.isLoadingTestCases = true;
    this.testCasesError = false;
    this.restService
      .getTestCaseByVersionId(this.projectId, versionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const rows = response?.data?.testCases || [];
          this.testCaseOptions = rows.map((tc: any) => ({
            _id: tc._id,
            testCaseId: tc.testCaseId,
            testCase: tc.testCase,
          }));
          this.isLoadingTestCases = false;
        },
        error: () => {
          this.isLoadingTestCases = false;
          this.testCasesError = true;
          this.testCaseOptions = [];
        },
      });
  }
}
